import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Slideover';
import { settingsAPI } from '../../services/api';
import CrewForm from '../settings/forms/CrewForm';
import ResourceForm from '../settings/forms/ResourceForm';

/**
 * InlineCreate — create a field tech / crew / resource without leaving the
 * WO flow (§1.5). Inline-created entities persist to Settings so they appear
 * in the corresponding Settings page.
 *
 * PT-22 W3-fp1 F1b: for `crew` and `resource`, opens a Modal that renders
 * the same form as Settings (`CrewForm` / `ResourceForm`). Inline-created
 * records are now first-class in Settings (Rule 10's shape half — see
 * PT-22 W3 W3-BUG-3). For `tech`, keeps the compact name+email inline row
 * (tech invite is a distinct shape — role=field_tech, seat=viewer,
 * send_invite=true — not a settings-list add).
 *
 * For field techs, email is REQUIRED (WOs are electronic — §1.5).
 *
 * props:
 *   kind: 'tech' | 'crew' | 'resource'
 *   onCreated: (entity) => void   // called with the persisted entity
 */
export function InlineCreate({ kind, onCreated }) {
  const labels = {
    tech: 'field tech',
    crew: 'crew',
    resource: 'resource',
  };

  // Tech path: inline name + email row (unchanged; not touched by F1b).
  if (kind === 'tech') {
    return <InlineTechCreate label={labels.tech} onCreated={onCreated} />;
  }

  // Crew + resource path: modal with the Settings-shape form.
  return (
    <ModalCreate kind={kind} label={labels[kind]} onCreated={onCreated} />
  );
}

// ─── Modal path (crew + resource) ────────────────────────────────────────────

function ModalCreate({ kind, label, onCreated }) {
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async (payload) => {
    setErr('');
    const listType = kind === 'crew' ? 'crews' : 'resource_types';
    try {
      const res = await settingsAPI.addListItem(listType, payload);
      const entity = res?.item || res || { ...payload, id: res?.id };
      onCreated && onCreated(entity);
      setOpen(false);
    } catch (e) {
      // Re-throw so the child form renders the error inline; also mirror it
      // in Modal-scope so a next-render preservation is possible if desired.
      setErr(e?.message || `Failed to create ${label}.`);
      throw e;
    }
  };

  const handleCancel = () => {
    setErr('');
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-sm text-[color:var(--color-action-primary)] hover:text-[color:var(--color-action-primary-hover)]"
      >
        <PlusIcon className="h-4 w-4" /> New {label}
      </button>
      <Modal
        open={open}
        onClose={handleCancel}
        title={`Add ${label}`}
        width="md"
        footer={null}
      >
        {kind === 'crew' ? (
          <CrewForm value={null} onSave={handleSave} onCancel={handleCancel} />
        ) : (
          /* Resource: empty category → custom-category flow. The form itself
             shows the Category input so the operator picks/names one. */
          <ResourceForm
            item={null}
            category=""
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
        {err && (
          /* Belt & suspenders: form owns inline error, but if the parent's
             mirror is populated (throw path), surface it here too. */
          <p className="mt-2 text-xs text-red-600" role="alert">{err}</p>
        )}
      </Modal>
    </>
  );
}

// ─── Inline path (tech only) ─────────────────────────────────────────────────

function InlineTechCreate({ label, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const reset = () => {
    setName(''); setEmail(''); setErr(''); setOpen(false);
  };

  const save = async () => {
    setErr('');
    if (!name.trim()) { setErr('Name is required.'); return; }
    if (!email.trim()) {
      setErr('Email is required — work orders are sent electronically.');
      return;
    }
    setSaving(true);
    try {
      const res = await settingsAPI.addUser({
        name: name.trim(),
        email: email.trim(),
        role: 'field_tech',
        seat_type: 'viewer',
        send_invite: true,
      });
      const entity = res?.user || res || { name: name.trim(), email: email.trim(), role: 'field_tech' };
      onCreated && onCreated(entity);
      reset();
    } catch (e) {
      setErr(e?.message || `Failed to create ${label}.`);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-sm text-[color:var(--color-action-primary)] hover:text-[color:var(--color-action-primary-hover)]"
      >
        <PlusIcon className="h-4 w-4" /> New {label}
      </button>
    );
  }

  return (
    <div className="card bg-gray-50 p-3 space-y-2">
      <Input
        autoFocus
        placeholder={`${label} name`}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        type="email"
        placeholder="email (required)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} loading={saving}>Add & invite</Button>
        <Button size="sm" variant="ghost" onClick={reset}>Cancel</Button>
      </div>
    </div>
  );
}

export default InlineCreate;
